import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import _ from 'lodash';
import styled from 'styled-components/macro';

import { fontkoKR, IconVolumeOn } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import GTM from '@mirinae/apis/GoogleAnalytics';
import { classes } from '@mirinae/react-ui';
import { lessonsPalette, zIndex } from '@mirinae/lessons/modules/defines/styles';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import { FormattedText } from '@mirinae/lessons/components/lesson/elements/Text';
import { Graphic, PlayAudioButton } from './WordCard';
import { playAzureTTS, playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import { azureVoicings, voicings } from '@mirinae/lessons/components/lesson/elements/LetterCard';

const Container = styled.div`
    flex-direction: column;
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

const GoalBox = styled.div`
    border: 1px solid ${lessonsPalette.grey[4]};
    border-radius: 30px;
    width: 100%;
    margin-bottom: 8px;
    padding: 12px 5px;
    text-align: center;
`;

const GoalSentenceContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 13px;
    // margin-bottom: 25px;

    ${os.isOthers ? `
        padding: 27px 40px;
        width: 90%;
    ` : `
        padding: 15px;
        width: 90%;
    `}
`;

const Sentence = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid ${lessonsPalette.grey[4]};
    border-radius: 13px;
    width: fit-content;
    color: ${lessonsPalette.grey[2]};
  
    ${os.isOthers ? `
        padding: 27px 55px;
        margin-bottom: 25px;
    ` : `
        padding: 15px 15px;
        margin-bottom: 25px;
    `}
`;

const SentenceSlots = styled.div`
    min-height: 50px;
    word-wrap: break-word;
    word-break: keep-all;
    padding-bottom: 6px;
    font-size: ${props => parseInt(props.scale * 20, 10)}px;
`;

const MakeTheWordSentenceSlots = styled(SentenceSlots)`
    ${props => props.complete && (`
        position: absolute;
        top: ${os.isOthers ? 96 : 195}px;
        font-size: 250%;
    `)}
`;

const SentenceSlot = styled.span`
    font-family: ${fontkoKR};
    // font-size: 24px;
    white-space: pre;
    display: inline-block;
    font-weight: bold;
    color: ${lessonsPalette.darken[1]};
    ${props => (props.frag.top === ' ' && 'margin: 6px;')}
`;

const EmptySlot = styled.span`
    width: ${props => props.lineWidth}px;
    border-bottom: 2px solid ${lessonsPalette.grey[4]};
    margin: 0 3px 3px;
    display: inline-block;
    vertical-align: bottom;
`;

// const SpaceSlot = styled.span`
//     width: 0.25em;  // width of a space in most character sets
//     display: inline-block;
// `;

// const SentenceEnglish = styled.div`
//     padding: 5px 8px 0;
//     font-size: 16px;
//     text-align: center;
//     color: ${lessonsPalette.grey[2]};
// `;

const ChoiceWords = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    visibility: ${props => (props.show ? 'visible' : 'hidden')};
    transform: scale(${props => props.scale});

    ${!os.isOthers && `
        flex-wrap: wrap;
    `}
`;

const ChoiceWord = styled.div`
    user-select: none;
    padding: 15px;
    border: 1px solid ${lessonsPalette.grey[5]};
    box-shadow: 3px 3px 7px rgba(159, 188, 186, 0.4);
    border-radius: 10px;
    font-size: 22px;
    font-weight: 600;
    color: ${lessonsPalette.grey[0]};
    cursor: pointer;
    animation: ${props => (props.wiggle ? 'wiggle 0.25s' : '')};
    animation-iteration-count: 3;
    visibility: ${props => (props.complete ? 'hidden' : props.show ? 'visible' : 'hidden')};
    white-space: nowrap;
    background-color: ${lessonsPalette.primary.white};
  
    ${os.isOthers ? `
        margin: 15px;
        &:nth-child(2n) {
            position: relative;
            top: 45px;
        }
    ` : `
        margin: 10px;
    `}

    &:hover {
        background-color: ${lessonsPalette.lessons[4]};
        border-color: ${lessonsPalette.darken[0]};
        box-shadow: none;
    }

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
        5% { transform: rotate(-5deg); }
       80% { transform: rotate(5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const BadChoiceWord = styled(ChoiceWord)`
    border-color: ${lessonsPalette.lessons[2]};
    background-color: ${lessonsPalette.lessons[3]};
    box-shadow: unset;

    &:hover {
        border-color: ${lessonsPalette.lessons[2]};
        background-color: ${lessonsPalette.lessons[3]};
        box-shadow: none;
    }
`;

const UnscrambleLine = styled.hr`
    width: 100%;
    margin-bottom: 11px;
    margin-top: -5px;
    border: solid ${lessonsPalette.grey[4]};
    border-width: 2px 0 0;
`;

const MakeTheWordContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    //align-items: center;
    //width: 100%;
    //gap: 60px;
  
    .${classes.desktop} & {
        flex-direction: row;
        position: relative;
        align-items: center;
        width: 100%;
        gap: 60px;
    }
  
    ${Sentence} {
        border: none;
        margin: 0;
        padding: 0;
        height: 88px;
        justify-content: end;
    }
  
    ${ChoiceWords} {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 16px;
        max-width: 346px;
    }
  
    ${ChoiceWord} {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 103px;
        height: 90px;
        padding: 0;
        margin: 0;
        top: 0; 
      
        .${classes.mobile} & {
            width: 100px;
            height: 62px;
        }
    }
`;

const TargetCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 240px;
    height: 100%;
    background:  ${lessonsPalette.primary.white};
    border: 1px solid ${lessonsPalette.grey[6]};
    box-sizing: border-box;
    box-shadow: ${props => (props.playing ? 'none' : '4px 4px 13px rgba(0, 0, 0, 0.07)')};
    border-radius: 10px;
    cursor: pointer;
    transition: all 350ms linear;

    &:hover {
        box-shadow: ${props => (props.playing ? 'none' : '4px 4px 13px 0px rgba(0, 0, 0, 0.20)')};
    }
  
    .${classes.mobile} & { 
        height: 165px;
        max-width: calc(100vw - 40px);
    }
`;

const AnswerAndChoices = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const PhraseSequence = ({ element }) => {
    const player = useContext(PlayerContext);
    const { 
        steps: stepField, 
        otherChoices: otherChoicesField,
        english,
        style,
    } = element.fieldsByName;

    const steps = stepField.listItems;
    const otherChoices = otherChoicesField.listItems;

    const picks = useMemo(() => _.chain(steps)
        .filter((item) => item.choice !== '')
        .map((item) => item.choice)
        .concat(otherChoices.map((item) => item.fragment))
        .shuffle()
        .value(), []);

    const [cursor, setCursor] = useState(0); // moves through the steps
    const [pickedItems, setPickedItems] = useState(new Set());
    const [complete, setComplete] = useState(false);
    const [badPick, setBadPick] = useState(null);
    const [choiceWordsScale, setChoiceWordsScale] = useState(1);
    const [sentenceSlotScale, setSentenceSlotScale] = useState(1); // not in use, for now
    const textRef = useRef(null);
    const choiceWordsRef = useRef(null);

    useEffect(() => {
        if (choiceWordsRef.current) {
            const ratio = choiceWordsRef.current.getBoundingClientRect().width / choiceWordsRef.current.parentElement.getBoundingClientRect().width;
            if (ratio > 1) {
                setChoiceWordsScale(1 / ratio);
            }
        }
    }, [element]);

    const getFinishedState = () => {
        return steps[steps.length - 1].step;
    };

    const checkForSuccess = () => {
        if (cursor === steps.length - 1 && !complete) {
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
            setComplete(true);
        }
    };

    const onClick = (chars) => {
        setBadPick(null);
        if (steps[cursor].choice === chars) {
            setPickedItems(pickedItems.add(chars));
            setCursor(cursor + 1);
            // changes size too late, would need to add resize observer, maybe post MVP
            // if (textRef.current && textRef.current.getBoundingClientRect().height > 2 * 20) { // todo: detect word-wrap crude heuristic
            //     setSentenceSlotScale(0.85);
            // }
        } else {
            setBadPick(chars);
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
            player.postEvent({ action: 'error' }, element);
        }
    };

    checkForSuccess();

    const renderGoalCheck = () => {
        return (
            <Element element={element}>
                <Container>
                    <GoalBox>
                        <FormattedText text={english.value} styles={english.styles} style={{ fontSize: 20, fontWeight: 600 }} />
                    </GoalBox>
                    <GoalSentenceContainer>
                        { complete ? <SuccessOverlay top={-55} /> : null}
                        <SentenceSlots complete={complete} ref={textRef} scale={sentenceSlotScale}>
                            { steps[cursor].step.split('').map((frag, i) => (
                                frag === '_' ? <EmptySlot key={i} lineWidth={22} />
                                    : <SentenceSlot key={i} complete={complete} frag={frag} dangerouslySetInnerHTML={{ __html: frag }} />
                            ))}
                        </SentenceSlots>
                        <UnscrambleLine />
                    </GoalSentenceContainer>
    
                    <ChoiceWords ref={choiceWordsRef} scale={choiceWordsScale}>
                        { picks.map((frag, i) => {
                            const isBadPick = frag === badPick;
                            const WordType = isBadPick ? BadChoiceWord : ChoiceWord;
                            return (
                                <WordType
                                    data-t={`choice-${frag}`} complete={complete} show={!pickedItems.has(frag)} wiggle={isBadPick} key={i}
                                    onClick={() => onClick(frag, i)}>{frag}
                                </WordType>
                            );
                        })}
                    </ChoiceWords>
                </Container>
            </Element>
        );
    };

    const renderStandard = () => {
        return (
            <Element element={element}>
                <Container>
                    <Sentence>
                        { complete ? <SuccessOverlay top={-55} /> : null}
                        <SentenceSlots complete={complete} ref={textRef} scale={sentenceSlotScale}>
                            { steps[cursor].step.split('').map((frag, i) => (
                                frag === '_' ? <EmptySlot key={i} lineWidth={22} />
                                    : <SentenceSlot key={i} complete={complete} frag={frag} dangerouslySetInnerHTML={{ __html: frag }} />
                            ))}
                        </SentenceSlots>
                        { style != null && style.value === 'Unscramble' ? <UnscrambleLine /> : null}
                        <FormattedText text={english.value} styles={english.styles} style={{ textAlign: 'center' }} />
                    </Sentence>
    
                    <ChoiceWords ref={choiceWordsRef} scale={choiceWordsScale}>
                        { picks.map((frag, i) => {
                            const isBadPick = frag === badPick;
                            const WordType = isBadPick ? BadChoiceWord : ChoiceWord;
                            return (
                                <WordType
                                    data-t={`choice-${frag}`} complete={complete} show={!pickedItems.has(frag)} wiggle={isBadPick} key={i}
                                    onClick={() => onClick(frag, i)}>{frag}
                                </WordType>
                            );
                        })}
                    </ChoiceWords>
                </Container>
            </Element>
        );
    };

    const renderMakeTheWord = () => {
        const svgEmbedRef = useRef(null);
        const [audioEl, setAudioEl] = useState(null);
        const [playing, setPlaying] = useState(false);

        const playAudio = (e) => {
            setPlaying(true);
            // playTTS(audioEl, element.fieldsByName.letter.value, e, voicings[element.fieldsByName?.voicing?.value || 'Male slow']);
            playAzureTTS(audioEl, element.fieldsByName.word.value, e, azureVoicings[element.fieldsByName?.voicing?.value || 'Female slow']);
            e.stopPropagation();
            GTM.userAction('word-audio', { word: element.fieldsByName.word.value }, 'lessons');
        };

        return (
            <Element element={element}>
                <MakeTheWordContainer>
                    <TargetCard className="word-card" onClick={playAudio} playing={playing}>
                        <PlayAudioButton type="button" onClick={playAudio}><IconVolumeOn color={playing ? '#48A9A6' : '#525252'} /></PlayAudioButton>
                        { element.fieldsByName.graphic.url &&
                            <Graphic graphic={element.fieldsByName.graphic} svgEmbedRef={svgEmbedRef} scale={element.fieldsByName.scale} /> }
                    </TargetCard>
                    <AnswerAndChoices>
                        <Sentence>
                            { complete && <SuccessOverlay top={22} screenWidth="680px" left={1} height="300px" /> }
                            <MakeTheWordSentenceSlots complete={complete} ref={textRef} scale={sentenceSlotScale} >
                                { steps[cursor].step.split('').map((frag, i) => (
                                    frag === '_' ? <EmptySlot key={i} lineWidth={22} />
                                        : <SentenceSlot key={i} complete={complete} frag={frag} dangerouslySetInnerHTML={{ __html: frag }} />
                                ))}
                            </MakeTheWordSentenceSlots>
                        </Sentence>

                        <ChoiceWords ref={choiceWordsRef} scale={choiceWordsScale}>
                            { picks.map((frag, i) => {
                                const isBadPick = frag === badPick;
                                const WordType = isBadPick ? BadChoiceWord : ChoiceWord;
                                return (
                                    <WordType
                                        data-t={`choice-${frag}`} complete={complete} show={!pickedItems.has(frag)} wiggle={isBadPick} key={i}
                                        onClick={() => onClick(frag, i)}>{frag}
                                    </WordType>
                                );
                            })}
                        </ChoiceWords>
                    </AnswerAndChoices>
                </MakeTheWordContainer>
                <audio
                    ref={setAudioEl}
                    onEnded={() => setPlaying(false)}
                />
            </Element>
        );
    };

    const renderer = {
        'Goal check': renderGoalCheck,
        'Make the word': renderMakeTheWord,
    }[style.value] || renderStandard;

    return renderer();
};

export default PhraseSequence;
